import React from "react";
import { IconButton, Stack, TextField, Typography } from "@mui/material";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Div from "@jumbo/shared/Div";
import { useTranslation } from "react-i18next";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

function getDisplayName(item, lang) {
  if (!item) return "";
  const over =
    item?.nameOverrideByLang?.[lang] ??
    item?.nameOverrideByLang?.["sk-SK"] ??
    item?.nameOverrideByLang?.["en-US"];
  if (typeof over === "string" && over) return over;
  const base = item?.baseName;
  if (typeof base === "string" && base) return base;
  if (base && typeof base === "object")
    return base[lang] ?? base["sk-SK"] ?? base["en-US"] ?? "";
  return "";
}

function getDisplayPrice(item) {
  if (item?.priceOverride != null && item?.priceOverride !== "")
    return Number(item.priceOverride);
  return item?.basePrice != null ? Number(item.basePrice) : "";
}

const MenuBoardProductRow = ({
  item,
  itemSortId,
  lang,
  languages,
  showImage,
  onUpdate,
  onRemove,
}) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: itemSortId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const displayPrice = getDisplayPrice(item);
  const displayName = getDisplayName(item, lang);

  return (
    <div ref={setNodeRef} style={style}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          py: 0.75,
          px: 1,
          borderBottom: 1,
          borderColor: "divider",
          "&:last-child": { borderBottom: 0 },
        }}
      >
        <Div
          {...attributes}
          {...listeners}
          sx={{ cursor: "grab", touchAction: "none", color: "text.secondary" }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Div>
        {showImage && item?.imageUrl ? (
          <img
            src={item.imageUrl}
            alt=""
            width={40}
            height={40}
            style={{ objectFit: "cover", borderRadius: 4 }}
          />
        ) : null}
        <Div sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            size="small"
            fullWidth
            value={displayName}
            onChange={(e) =>
              onUpdate("nameOverrideByLang", {
                ...(item.nameOverrideByLang || {}),
                [lang]: e.target.value,
              })
            }
            placeholder={
              typeof item?.baseName === "string" ? item.baseName : ""
            }
          />
        </Div>
        <TextField
          size="small"
          type="number"
          inputProps={{ min: 0, step: 0.01 }}
          value={displayPrice === "" ? "" : displayPrice}
          onChange={(e) => {
            const v = e.target.value;
            onUpdate("priceOverride", v === "" ? null : parseFloat(v));
          }}
          sx={{ width: 90 }}
        />
        <IconButton
          size="small"
          onClick={() => onRemove(item.id)}
          aria-label={t("buttons.delete")}
        >
          <DeleteForeverIcon fontSize="small" />
        </IconButton>
      </Stack>
    </div>
  );
};

export default MenuBoardProductRow;
